import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Grid,
  Box,
  Typography,
  TextField,
  FormControl,
  Button,
  InputAdornment,
  IconButton,
} from '@mui/material';
import logoIcon from '../../../../../assets/common-assets/docu-transcribe-logo.png';
import Visibility from '../../../../../assets/common-assets/eye.svg';
import VisibilityOff from '../../../../../assets/common-assets/eye-off.svg';
import {
  ADD_USER,
  INVALID_INPUT_REGEX,
  getHelperText,
} from '../../../../../constants/staticContent';
import { Root, classes } from './utils';
import { lightPalette } from '../../../../../theme';
import { addUser, verifyToken } from '../../../../../services/addMemberApi';
import CountryDropdown from '../../../account-settings/CountryDropdown';

const AddUsers = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = params;

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNo: '',
    password: '',
    confirmPassword: '',
    country: '',
  });
  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    phoneNo: false,
    password: false,
    confirmPassword: false,
    country: false,
  });
  const [id, setId] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [email, setEmail] = useState('');
  const [showPassword, setShowPassword] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState('');
  const [countryValid, setCountryValid] = useState(true);
  const [isSuperAdminRegistration, setIsSuperAdminRegistration] =
    useState(false);
  const [countryList, setCountryList] = useState({ key: '', label: '' });

  useEffect(() => {
    if (location.pathname === `/addNewMembersSASide/${token}`) {
      setIsSuperAdminRegistration(true);
    } else {
      setIsSuperAdminRegistration(false);
    }
  }, []);
  useEffect(() => {
    const validate = async () => {
      const response = await verifyToken(token);
      setId(response?.data?.user?.id);
      setEmail(response?.data?.user?.email);
      if (location.pathname !== `/addNewMembersSASide/${token}`) {
        setOrganizationName(response?.data?.user?.organization_name);
      }
    };

    validate();
  }, [token]);
  const newConfirmSimilarity = (newPass, confirmPass) =>
    newPass === confirmPass;
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConformPassword = () =>
    setShowConfirmPassword((show) => !show);

  const validateEmptyPassword = (password) => password !== '';
  const validateFirstName = (fname) => {
    const firstNameRegex = INVALID_INPUT_REGEX.NAME;
    return firstNameRegex.test(fname);
  };
  const validateLastName = (lname) => {
    const lastNameRegex = INVALID_INPUT_REGEX.NAME;
    return lastNameRegex.test(lname);
  };

  const validatePhone = (phone) => {
    const phoneRegex = INVALID_INPUT_REGEX.PHONE_NO;
    return phoneRegex.test(phone);
  };

  const validatePassword = (password) => {
    const passwordRegex = INVALID_INPUT_REGEX.PASSWORD;
    return passwordRegex.test(password);
  };

  const validateCountry = (countryValue) => {
    if (!countryValue) {
      setCountryValid(false);
      return false;
    }
    setCountryValid(true);
    return true;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const ValidateValue = name === 'phoneNo' ? value.replace(/\D/g, '') : value;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: ValidateValue,
    }));
    if (
      name === 'firstName' ||
      name === 'lastName' ||
      name === 'password' ||
      name === 'confirmPassword' ||
      name === 'phoneNo'
    ) {
      const isValidFname = validateFirstName(value);
      const isValidLname = validateLastName(value);
      const isValidPhone = validatePhone(ValidateValue);
      const isValidPassword = validatePassword(value);
      const isValidConfirmPassword = value === formData.password;
      setErrors((prevErrors) => ({
        ...prevErrors,
        firstName: name === 'firstName' ? !isValidFname : prevErrors.firstName,
        lastName: name === 'lastName' ? !isValidLname : prevErrors.lastName,
        phoneNo: name === 'phoneNo' ? !isValidPhone : prevErrors.phoneNo,
        password: name === 'password' ? !isValidPassword : prevErrors.password,
        confirmPassword:
          name === 'confirmPassword'
            ? !isValidConfirmPassword
            : prevErrors.confirmPassword,
      }));
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    const fnameValid = validateFirstName(formData.firstName);
    const lnameValid = validateLastName(formData.lastName);
    const phoneValid = validatePhone(formData.phoneNo);
    const passwordValid = validatePassword(formData.password);
    const confirmPasswordValid = validateEmptyPassword(
      formData.confirmPassword,
    );
    const countriesValid = validateCountry(countryList?.label);
    const similarityValid = newConfirmSimilarity(
      formData.password,
      formData.confirmPassword,
    );

    setErrors({
      firstName: !fnameValid,
      lastName: !lnameValid,
      phoneNo: !phoneValid,
      password: !passwordValid,
      confirmPassword: !confirmPasswordValid || !similarityValid,
      country: !countriesValid,
    });

    const { firstName } = formData;
    const { lastName } = formData;
    const { phoneNo } = formData;
    const { password } = formData;
    const { confirmPassword } = formData;
    const country = countryList?.label;
    if (
      fnameValid &&
      lnameValid &&
      phoneValid &&
      passwordValid &&
      confirmPasswordValid &&
      similarityValid &&
      countryValid
    ) {
      const response = await addUser({
        firstName,
        lastName,
        phoneNo,
        password,
        confirmPassword,
        country,
        id,
        token,
      });
      if (response) {
        navigate('/login');
      }
    }
  };

  return (
    <Root>
      <Grid container className={classes.mainGrid}>
        <Grid item xs={5} className={classes.innerGrid}>
          <Box
            className={classes.logoBox}
            component="img"
            src={logoIcon}
            alt="logoIcon"
          />

          <Box className={classes.formMainBox}>
            <FormControl className={classes.formMainFormControl}>
              <TextField
                autoFocus
                className={classes.formAllTextFields}
                required
                label={ADD_USER.TEXTFIELD.FIRST_NAME}
                id="firstName"
                name="firstName"
                autoComplete="firstName"
                onChange={handleInputChange}
                value={formData.firstName}
                error={errors.firstName}
                helperText={getHelperText(
                  'firstName',
                  'firstName',
                  errors,
                  formData,
                )}
              />
              <TextField
                className={classes.formAllTextFields}
                required
                label={ADD_USER.TEXTFIELD.LAST_NAME}
                id="lastName"
                name="lastName"
                autoComplete="lastName"
                onChange={handleInputChange}
                value={formData.lastName}
                error={errors.lastName}
                helperText={getHelperText(
                  'lastName',
                  'lastName',
                  errors,
                  formData,
                )}
              />
              <TextField
                className={classes.formAllTextFields}
                required
                disabled
                variant="filled"
                label={ADD_USER.TEXTFIELD.EMAIL}
                id="email"
                name="email"
                autoComplete="email"
                value={email}
                InputProps={{
                  readOnly: true,
                }}
              />
              <TextField
                className={classes.formAllTextFields}
                type="text"
                required
                label={ADD_USER.TEXTFIELD.PHONE_NO}
                name="phoneNo"
                id="phoneNo"
                autoComplete="phoneNo"
                onChange={handleInputChange}
                value={formData.phoneNo}
                error={errors.phoneNo}
                helperText={getHelperText(
                  'phoneNo',
                  'phoneNo',
                  errors,
                  formData,
                )}
              />
              <TextField
                className={classes.formAllTextFields}
                required
                label={ADD_USER.TEXTFIELD.PASSWORD}
                id="password"
                name="password"
                autoComplete="password"
                onChange={handleInputChange}
                value={formData.password}
                error={errors.password}
                helperText={getHelperText(
                  'password',
                  'password',
                  errors,
                  formData,
                )}
                type={showPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? (
                          <Box
                            component="img"
                            className={classes.eyeIconForPassword}
                            src={Visibility}
                            alt="EyeOnIcon"
                          />
                        ) : (
                          <Box
                            component="img"
                            className={classes.eyeIconForPassword}
                            src={VisibilityOff}
                            alt="EyeOffIcon"
                          />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                className={classes.formAllTextFields}
                required
                label={ADD_USER.TEXTFIELD.CONFIRM_PASSWORD}
                id="confirmPassword"
                name="confirmPassword"
                autoComplete="confirmPassword"
                onChange={handleInputChange}
                value={formData.confirmPassword}
                error={errors.confirmPassword}
                helperText={getHelperText(
                  'confirmPassword',
                  'confirmPassword',
                  errors,
                  formData,
                )}
                type={showConfirmPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowConformPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <Box
                            component="img"
                            className={classes.eyeIconForPassword}
                            src={Visibility}
                            alt="EyeOnIcon"
                          />
                        ) : (
                          <Box
                            component="img"
                            className={classes.eyeIconForPassword}
                            src={VisibilityOff}
                            alt="EyeOffIcon"
                          />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {!isSuperAdminRegistration ? (
                <TextField
                  className={classes.formAllTextFields}
                  required
                  disabled
                  variant="filled"
                  label={ADD_USER.TEXTFIELD.ORGANIZATION_NAME}
                  id="organization_name"
                  name="organization_name"
                  autoComplete="organization_name"
                  value={organizationName}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              ) : null}
              <FormControl className={classes.formAllTextFields}>
                <CountryDropdown
                  countryList={countryList}
                  setCountryList={setCountryList}
                  countryValid={countryValid}
                  setCountryValid={setCountryValid}
                  setFormData={setFormData}
                />
              </FormControl>
              <Button
                type="submit"
                disabled={email === undefined}
                onClick={handleSubmit}
                variant="contained"
                className={classes.submitButton}
              >
                <Typography variant="h5">{ADD_USER.SUBMIT_BUTTON}</Typography>
              </Button>
            </FormControl>
          </Box>

          <Typography
            variant="h5"
            color={lightPalette.dynamicBlack.semiLight}
            fontWeight="400"
          >
            {addUser.FOOTER}
          </Typography>
        </Grid>
      </Grid>
    </Root>
  );
};

export default AddUsers;
